import { useEffect, useMemo, useRef, useState } from "react";
import { ThumbsUp, ThumbsDown, MessageCircle, Eye, Megaphone, Check, X } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import VerifiedBadge from "./VerifiedBadge";
import EmojiPickerButton from "./EmojiPickerButton";
import ShareMenu from "./ShareMenu";
import ReactionPicker from "./ReactionPicker";
import ElderBadge from "./ElderBadge";
import PostMenu from "./PostMenu";
import { hideOnError } from "../lib/imgFallback";
import { displayName, displayInitial } from "../lib/displayName";
import { toast } from "sonner";

export default function PostCard({ post, onUpdate, onRemove }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [comments, setComments] = useState([]);
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(post.content || "");
  const [editBusy, setEditBusy] = useState(false);
  const commentRef = useRef(null);
  const viewedRef = useRef(false);

  const liked = user && post.likes?.includes(user.id);
  const disliked = user && post.dislikes?.includes(user.id);
  const reactions = post.reactions || {};
  const myReaction = user ? reactions[user.id] : null;
  const isOwner = user && user.id === post.userId && !post.sponsored;

  const [reactionOpen, setReactionOpen] = useState(false);
  const reactionAnchor = useRef(null);

  // Aggregate reaction counts (across all users on this post)
  const reactionCounts = useMemo(() => {
    const c = {};
    Object.values(reactions).forEach((e) => { c[e] = (c[e] || 0) + 1; });
    return Object.entries(c).sort((a, b) => b[1] - a[1]);
  }, [reactions]);
  const totalReactions = reactionCounts.reduce((s, [, n]) => s + n, 0);

  // Track unique view once per mount per user
  useEffect(() => {
    if (!user || post.sponsored || viewedRef.current || !post.id || post.id.startsWith("ad_")) return;
    viewedRef.current = true;
    api.post(`/posts/${post.id}/view`)
      .then(({ data }) => onUpdate && onUpdate({ ...post, views: data.views }))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, post.id]);

  const toggleDislike = async () => {
    if (!user) return;
    const { data } = await api.post(`/posts/${post.id}/dislike`);
    onUpdate && onUpdate({ ...post, likes: data.likes, dislikes: data.dislikes, reactions: data.reactions || {} });
  };
  const setReaction = async (emoji) => {
    if (!user) return;
    setReactionOpen(false);
    try {
      const { data } = await api.post(`/posts/${post.id}/react`, { emoji });
      onUpdate && onUpdate({ ...post, likes: data.likes, dislikes: data.dislikes, reactions: data.reactions });
    } catch { /* noop */ }
  };
  const clearReaction = () => setReaction(null);
  const loadComments = async () => {
    const { data } = await api.get(`/posts/${post.id}/comments`);
    setComments(data); setShowComments(true);
  };
  const submitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    const { data } = await api.post(`/posts/${post.id}/comments`, { content: newComment });
    setComments([...comments, data]); setNewComment("");
    onUpdate && onUpdate({ ...post, commentCount: (post.commentCount || 0) + 1 });
  };
  const insertEmojiToComment = (emoji) => {
    setNewComment((c) => c + emoji);
    setTimeout(() => commentRef.current?.focus(), 0);
  };
  const boostThis = () => nav(`/ads?boost=${post.id}`);

  const viewCount = post.views || (post.viewedBy?.length || 0);

  return (
    <article className={`parchment p-6 ${post.sponsored ? "border-secondary/40 bg-secondary/[0.02]" : ""}`} data-testid={`post-${post.id}`}>
      {post.sponsored && (
        <div className="mb-3 flex items-center justify-between">
          <span className="label-mystic text-secondary">⟡ Sponsored</span>
          <span className="text-[10px] text-foreground/40 uppercase tracking-wider">Promoted</span>
        </div>
      )}
      <div className="flex items-center gap-3 mb-4">
        <Link to={`/profile/${post.userId}`} className="w-10 h-10 rounded-sm bg-secondary/10 border border-secondary/30 flex items-center justify-center font-serif text-secondary">
          {displayInitial({ ...post.provider, user: post.user })}
        </Link>
        <div className="flex-1">
          <Link to={`/profile/${post.userId}`} className="font-serif text-lg hover:text-secondary inline-flex items-center gap-1.5" data-testid={`post-author-${post.id}`}>
            {displayName({ ...post.provider, user: post.user })}
            <VerifiedBadge size={14} show={post.provider?.verified} />
            <ElderBadge size={14} show={post.provider?.elder} index={post.provider?.elderIndex} />
          </Link>
          <div className="label-mystic">{post.sponsored ? "Sponsored placement" : new Date(post.createdAt).toLocaleString()}</div>
        </div>
        {post.type === "LIVE" && (
          <span className="text-xs px-2 py-1 bg-primary/20 text-primary border border-primary/40 uppercase tracking-wider">● Live</span>
        )}
        {isOwner && user?.role === "PROVIDER" && (
          <button onClick={boostThis} className="text-xs px-3 py-1.5 border border-primary/50 text-primary rounded-sm uppercase tracking-wider flex items-center gap-1.5 hover:bg-primary/10"
            title="Boost this post" data-testid={`boost-btn-${post.id}`}>
            <Megaphone size={12} strokeWidth={1.5} /> Boost
          </button>
        )}
        {!post.sponsored && (
          <PostMenu
            post={post}
            onUpdated={(np) => onUpdate?.(np)}
            onDeleted={(pid) => onRemove?.(pid)}
            onHidden={(pid) => onRemove?.(pid)}
            onEdit={() => { setEditText(post.content || ""); setEditing(true); }}
          />
        )}
      </div>
      {editing ? (
        <div className="mb-4">
          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            rows={Math.max(3, Math.min(10, editText.split("\n").length + 1))}
            className="w-full bg-input/40 border border-secondary/30 rounded-sm p-3 text-sm focus:outline-none focus:border-secondary"
            data-testid={`edit-input-${post.id}`}
            autoFocus
          />
          <div className="flex justify-end gap-2 mt-2">
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-sm uppercase tracking-wider text-xs border border-secondary/30 text-foreground/80"
              data-testid={`edit-cancel-${post.id}`}
            >
              <X size={12} strokeWidth={1.5} /> Cancel
            </button>
            <button
              type="button"
              disabled={editBusy || editText.trim() === (post.content || "")}
              onClick={async () => {
                setEditBusy(true);
                try {
                  const { data } = await api.patch(`/posts/${post.id}`, { content: editText });
                  onUpdate?.({ ...post, ...data });
                  setEditing(false);
                  toast.success("Post updated");
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Failed");
                } finally { setEditBusy(false); }
              }}
              className="flex items-center gap-1 px-3 py-1.5 bg-primary text-primary-foreground rounded-sm uppercase tracking-wider text-xs disabled:opacity-50"
              data-testid={`edit-save-${post.id}`}
            >
              <Check size={12} strokeWidth={1.5} /> Save
            </button>
          </div>
        </div>
      ) : post.content ? (
        <p className="text-foreground/90 leading-relaxed whitespace-pre-wrap mb-4">
          {post.content}
          {post.editedAt && <span className="ml-2 text-xs text-foreground/40" data-testid={`edited-marker-${post.id}`}>· edited</span>}
        </p>
      ) : null}
      {post.mediaUrl && post.type === "IMAGE" && (
        <img src={post.mediaUrl} alt="" onError={hideOnError} className="w-full max-h-[600px] object-cover border border-secondary/15 mb-4" />
      )}
      {post.mediaUrl && post.type === "VIDEO" && (
        <video src={post.mediaUrl} controls className="w-full border border-secondary/15 mb-4" />
      )}
      {post.linkUrl && (
        <a href={post.linkUrl} target="_blank" rel="noreferrer" className="block parchment p-3 mb-4 text-secondary hover:text-secondary/80 truncate">
          {post.linkUrl}
        </a>
      )}

      {/* Stats row — views + reactions count */}
      <div className="flex items-center justify-between text-xs text-foreground/55 mb-2">
        <div className="flex items-center gap-3 flex-wrap">
          {totalReactions > 0 && (
            <span className="flex items-center gap-0.5" data-testid={`reactions-summary-${post.id}`}>
              {reactionCounts.slice(0, 3).map(([e]) => <span key={e} className="text-base leading-none">{e}</span>)}
              <span className="ml-1">{totalReactions}</span>
            </span>
          )}
          {(post.likes?.length || 0) > 0 && <span className="flex items-center gap-1" data-testid={`likes-count-${post.id}`}><ThumbsUp size={10} strokeWidth={2} /> {post.likes.length}</span>}
          {(post.dislikes?.length || 0) > 0 && <span className="flex items-center gap-1" data-testid={`dislikes-count-${post.id}`}><ThumbsDown size={10} strokeWidth={2} /> {post.dislikes.length}</span>}
        </div>
        <span className="flex items-center gap-1" data-testid={`views-count-${post.id}`}><Eye size={11} strokeWidth={1.5} /> {viewCount} {viewCount === 1 ? "view" : "views"}</span>
      </div>

      <div className="gold-divider my-3" />
      {post.sponsored ? (
        <div className="flex items-center justify-between">
          <span className="text-xs text-foreground/50">Boosted by Cauldron Ads</span>
          <Link
            to={`/profile/${post.userId}`}
            onClick={() => post.adId && api.post(`/ads/${post.adId}/click`).catch(() => {})}
            className="bg-primary text-primary-foreground px-5 py-2 rounded-sm uppercase tracking-wider text-xs"
            data-testid={`sponsored-cta-${post.adId || post.id}`}
          >
            Visit Practitioner →
          </Link>
        </div>
      ) : (
        <div className="flex items-center gap-1 sm:gap-3 text-sm">
          <div ref={reactionAnchor} className="relative">
            <button onClick={() => myReaction ? clearReaction() : setReactionOpen((o) => !o)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-sm transition-colors ${myReaction || liked ? "text-primary bg-primary/10" : "text-foreground/70 hover:text-primary hover:bg-primary/5"}`}
              data-testid={`like-btn-${post.id}`}>
              {myReaction ? (
                <span className="text-lg leading-none" data-testid={`my-reaction-${post.id}`}>{myReaction}</span>
              ) : (
                <ThumbsUp size={16} strokeWidth={liked ? 2 : 1.5} fill={liked ? "currentColor" : "none"} />
              )}
              <span className="hidden sm:inline">{myReaction ? "Reacted" : "Like"}</span>
            </button>
            <ReactionPicker open={reactionOpen} anchorRef={reactionAnchor}
              onSelect={setReaction} onClose={() => setReactionOpen(false)} />
          </div>
          <button onClick={toggleDislike} className={`flex items-center gap-2 px-3 py-1.5 rounded-sm ${disliked ? "text-destructive bg-destructive/10" : "text-foreground/70 hover:text-destructive hover:bg-destructive/5"}`} data-testid={`dislike-btn-${post.id}`}>
            <ThumbsDown size={16} strokeWidth={disliked ? 2 : 1.5} fill={disliked ? "currentColor" : "none"} />
            <span className="hidden sm:inline">Dislike</span>
          </button>
          <button onClick={loadComments} className="flex items-center gap-2 px-3 py-1.5 rounded-sm text-foreground/70 hover:text-secondary hover:bg-secondary/10" data-testid={`comment-btn-${post.id}`}>
            <MessageCircle size={16} strokeWidth={1.5} />
            <span className="hidden sm:inline">Comment</span>
            <span className="text-xs">{post.commentCount || 0}</span>
          </button>
          <ShareMenu
            postId={post.id}
            postTitle={`${post.user?.name || "Cauldron"}'s post`}
            postContent={post.content || ""}
            testid={`share-btn-${post.id}`}
          />
        </div>
      )}
      {showComments && !post.sponsored && (
        <div className="mt-4 space-y-3">
          {comments.map((c, i) => (
            <CommentRow
              key={c.id}
              comment={c}
              currentUserId={user?.id}
              onChange={(next) =>
                setComments((cs) => cs.map((x, j) => (j === i ? next : x)))
              }
            />
          ))}
          <form onSubmit={submitComment} className="flex gap-2">
            <div className="flex-1 relative">
              <input
                ref={commentRef}
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a sacred reflection..."
                className="w-full bg-input border border-secondary/20 rounded-sm pl-3 pr-12 py-2 text-sm focus:outline-none focus:border-secondary"
                data-testid={`comment-input-${post.id}`}
              />
              <div className="absolute right-1 top-1/2 -translate-y-1/2">
                <EmojiPickerButton testid={`comment-emoji-${post.id}`} onEmojiClick={insertEmojiToComment} buttonClass="p-1.5" />
              </div>
            </div>
            <button className="px-4 py-2 bg-primary text-primary-foreground text-xs uppercase tracking-wider rounded-sm" data-testid={`comment-submit-${post.id}`}>Post</button>
          </form>
        </div>
      )}
    </article>
  );
}


/**
 * CommentRow — single comment with thumbs-up / thumbs-down voting.
 *
 * Vote semantics (drives backend POST /comments/{id}/vote):
 *   • Click up      → set vote to "up"   (toggles down off if active)
 *   • Click up again → clear vote (null)
 *   • Click down    → set vote to "down" (toggles up off if active)
 *   • Click down again → clear vote (null)
 *
 * Optimistic local update + server-authoritative reconciliation: we
 * compute the expected next state locally for instant feedback, then
 * replace with the server's actual counts on response.
 */
function CommentRow({ comment, currentUserId, onChange }) {
  const myVote = comment.myVote;
  const upCount = comment.upCount ?? 0;
  const downCount = comment.downCount ?? 0;

  const cast = async (clicked) => {
    if (!currentUserId) {
      toast.error("Sign in to vote");
      return;
    }
    // Clicking the same vote clears it. Clicking the opposite switches.
    const nextVote = myVote === clicked ? null : clicked;

    // Optimistic state
    const optimistic = { ...comment };
    if (myVote === "up") optimistic.upCount = Math.max(0, upCount - 1);
    if (myVote === "down") optimistic.downCount = Math.max(0, downCount - 1);
    if (nextVote === "up") optimistic.upCount = (optimistic.upCount ?? 0) + 1;
    if (nextVote === "down") optimistic.downCount = (optimistic.downCount ?? 0) + 1;
    optimistic.myVote = nextVote;
    onChange(optimistic);

    try {
      const { data } = await api.post(`/comments/${comment.id}/vote`, { vote: nextVote });
      onChange({ ...comment, ...data });
    } catch {
      // Roll back
      onChange(comment);
      toast.error("Couldn't record vote");
    }
  };

  return (
    <div
      className="text-sm border-l-2 border-secondary/20 pl-3"
      data-testid={`comment-row-${comment.id}`}
    >
      <div>
        <span className="font-serif text-secondary mr-2">{displayName({ userName: comment.userName, name: comment.userName })}</span>
        <span className="text-foreground/85">{comment.content}</span>
      </div>
      <div className="mt-1.5 flex items-center gap-3">
        <button
          type="button"
          onClick={() => cast("up")}
          className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded transition-colors ${
            myVote === "up"
              ? "text-emerald-300 bg-emerald-500/15"
              : "text-foreground/55 hover:text-emerald-300"
          }`}
          aria-label="Thumbs up"
          data-testid={`comment-up-${comment.id}`}
        >
          <ThumbsUp size={12} strokeWidth={2} />
          <span>{upCount || ""}</span>
        </button>
        <button
          type="button"
          onClick={() => cast("down")}
          className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded transition-colors ${
            myVote === "down"
              ? "text-red-300 bg-red-500/15"
              : "text-foreground/55 hover:text-red-300"
          }`}
          aria-label="Thumbs down"
          data-testid={`comment-down-${comment.id}`}
        >
          <ThumbsDown size={12} strokeWidth={2} />
          <span>{downCount || ""}</span>
        </button>
      </div>
    </div>
  );
}
